import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mic, Building2, Briefcase, ChevronRight, ChevronLeft, Check } from 'lucide-react';
type Role = 'artist' | 'venue' | 'agent' | null;
export function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState<Role>(null);
  const [formData, setFormData] = useState({
    name: '',
    genre: '',
    location: '',
    feeRange: '',
    capacity: '',
    budget: '',
    rosterSize: ''
  });
  const totalSteps = 6;
  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };
  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };
  const handleComplete = () => {
    navigate('/app');
  };
  return <div className="min-h-screen bg-background flex flex-col" data-id="element-6556">
      {/* Header */}
      <div className="p-6 flex items-center justify-between border-b border-border" data-id="element-6557">
        <div className="flex items-center gap-2" data-id="element-6558">
          <span className="text-xl font-bold text-textMain" data-id="element-6559">Booker</span>
          <div className="w-1.5 h-1.5 rounded-full bg-primary" data-id="element-6560"></div>
        </div>
        <div className="text-sm text-textMuted" data-id="element-6561">
          Step {step} of {totalSteps}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1 bg-surface" data-id="element-6562">
        <motion.div className="h-full bg-primary" initial={{
        width: 0
      }} animate={{
        width: `${step / totalSteps * 100}%`
      }} transition={{
        duration: 0.3
      }} data-id="element-6563" />
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6" data-id="element-6564">
        <div className="w-full max-w-2xl" data-id="element-6565">
          <AnimatePresence mode="wait" data-id="element-6566">
            {/* Step 1: Choose Role */}
            {step === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-6567">
                <h1 className="text-3xl md:text-4xl font-bold mb-4 text-center" data-id="element-6568">
                  Welcome to Booker
                </h1>
                <p className="text-lg text-textMuted mb-12 text-center" data-id="element-6569">
                  Let's get you set up. First, tell us what you do.
                </p>

                <div className="grid md:grid-cols-3 gap-4" data-id="element-6570">
                  {[{
                id: 'artist' as Role,
                icon: <Mic className="w-6 h-6" data-id="element-6571" />,
                title: 'Artist / Band',
                desc: 'Book tours and manage shows'
              }, {
                id: 'venue' as Role,
                icon: <Building2 className="w-6 h-6" data-id="element-6572" />,
                title: 'Venue / Promoter',
                desc: 'Fill dates and program events'
              }, {
                id: 'agent' as Role,
                icon: <Briefcase className="w-6 h-6" data-id="element-6573" />,
                title: 'Agent / Manager',
                desc: 'Run a roster at scale'
              }].map(option => <button key={option.id} onClick={() => setRole(option.id)} className={`p-6 rounded-xl border-2 transition-all text-left ${role === option.id ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:border-primary/50'}`} data-id="element-6574">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${role === option.id ? 'bg-primary/20 text-primary' : 'bg-background text-textMuted'}`} data-id="element-6575">
                        {option.icon}
                      </div>
                      <h3 className="text-lg font-semibold mb-2" data-id="element-6576">
                        {option.title}
                      </h3>
                      <p className="text-sm text-textMuted" data-id="element-6577">{option.desc}</p>
                    </button>)}
                </div>
              </motion.div>}

            {/* Step 2: Profile */}
            {step === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-6578">
                <h2 className="text-3xl font-bold mb-4" data-id="element-6579">Create your profile</h2>
                <p className="text-textMuted mb-8" data-id="element-6580">
                  Tell us a bit about yourself.
                </p>

                <div className="space-y-4" data-id="element-6581">
                  <div data-id="element-6582">
                    <label className="block text-sm font-medium mb-2" data-id="element-6583">
                      {role === 'artist' ? 'Artist / Band Name' : role === 'venue' ? 'Venue Name' : 'Agency Name'}
                    </label>
                    <input type="text" value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:outline-none focus:border-primary transition-colors" placeholder={role === 'artist' ? 'The Wildwoods' : role === 'venue' ? 'Higher Ground' : 'Paradigm Talent Agency'} data-id="element-6584" />
                  </div>

                  <div data-id="element-6585">
                    <label className="block text-sm font-medium mb-2" data-id="element-6586">
                      {role === 'artist' ? 'Genre' : role === 'venue' ? 'Primary Genre Focus' : 'Primary Genres'}
                    </label>
                    <input type="text" value={formData.genre} onChange={e => setFormData({
                  ...formData,
                  genre: e.target.value
                })} className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:outline-none focus:border-primary transition-colors" placeholder="Folk, Americana, Indie" data-id="element-6587" />
                  </div>

                  <div data-id="element-6588">
                    <label className="block text-sm font-medium mb-2" data-id="element-6589">
                      Location
                    </label>
                    <input type="text" value={formData.location} onChange={e => setFormData({
                  ...formData,
                  location: e.target.value
                })} className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:outline-none focus:border-primary transition-colors" placeholder="Burlington, VT" data-id="element-6590" />
                  </div>
                </div>
              </motion.div>}

            {/* Step 3: Details */}
            {step === 3 && <motion.div key="step3" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-6591">
                <h2 className="text-3xl font-bold mb-4" data-id="element-6592">Essential details</h2>
                <p className="text-textMuted mb-8" data-id="element-6593">
                  This helps us match you better.
                </p>

                <div className="space-y-4" data-id="element-6594">
                  {role === 'artist' && <>
                      <div data-id="element-6595">
                        <label className="block text-sm font-medium mb-2" data-id="element-6596">
                          Typical Fee Range
                        </label>
                        <input type="text" value={formData.feeRange} onChange={e => setFormData({
                    ...formData,
                    feeRange: e.target.value
                  })} className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:outline-none focus:border-primary transition-colors" placeholder="$1,500 - $3,000" data-id="element-6597" />
                      </div>
                      <div data-id="element-6598">
                        <label className="block text-sm font-medium mb-2" data-id="element-6599">
                          Preferred Venue Capacity
                        </label>
                        <input type="text" value={formData.capacity} onChange={e => setFormData({
                    ...formData,
                    capacity: e.target.value
                  })} className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:outline-none focus:border-primary transition-colors" placeholder="200 - 500" data-id="element-6600" />
                      </div>
                    </>}

                  {role === 'venue' && <>
                      <div data-id="element-6601">
                        <label className="block text-sm font-medium mb-2" data-id="element-6602">
                          Venue Capacity
                        </label>
                        <input type="text" value={formData.capacity} onChange={e => setFormData({
                    ...formData,
                    capacity: e.target.value
                  })} className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:outline-none focus:border-primary transition-colors" placeholder="400" data-id="element-6603" />
                      </div>
                      <div data-id="element-6604">
                        <label className="block text-sm font-medium mb-2" data-id="element-6605">
                          Typical Budget per Show
                        </label>
                        <input type="text" value={formData.budget} onChange={e => setFormData({
                    ...formData,
                    budget: e.target.value
                  })} className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:outline-none focus:border-primary transition-colors" placeholder="$2,000 - $5,000" data-id="element-6606" />
                      </div>
                    </>}

                  {role === 'agent' && <>
                      <div data-id="element-6607">
                        <label className="block text-sm font-medium mb-2" data-id="element-6608">
                          Current Roster Size
                        </label>
                        <input type="text" value={formData.rosterSize} onChange={e => setFormData({
                    ...formData,
                    rosterSize: e.target.value
                  })} className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:outline-none focus:border-primary transition-colors" placeholder="5 - 10 artists" data-id="element-6609" />
                      </div>
                      <div data-id="element-6610">
                        <label className="block text-sm font-medium mb-2" data-id="element-6611">
                          Typical Fee Range
                        </label>
                        <input type="text" value={formData.feeRange} onChange={e => setFormData({
                    ...formData,
                    feeRange: e.target.value
                  })} className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:outline-none focus:border-primary transition-colors" placeholder="$1,000 - $10,000" data-id="element-6612" />
                      </div>
                    </>}
                </div>
              </motion.div>}

            {/* Step 4: Availability */}
            {step === 4 && <motion.div key="step4" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} className="text-center" data-id="element-6613">
                <h2 className="text-3xl font-bold mb-4" data-id="element-6614">
                  Set your availability
                </h2>
                <p className="text-textMuted mb-8" data-id="element-6615">
                  {role === 'artist' ? 'When are you looking to tour?' : 'When do you have open dates?'}
                </p>

                <div className="bg-surface border border-border rounded-xl p-8 mb-6" data-id="element-6616">
                  <div className="text-sm text-textMuted mb-4" data-id="element-6617">
                    We'll add calendar integration in the next step
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-6618">
                    <div data-id="element-6619">
                      <label className="block text-sm font-medium mb-2" data-id="element-6620">
                        From
                      </label>
                      <input type="date" className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:border-primary transition-colors" data-id="element-6621" />
                    </div>
                    <div data-id="element-6622">
                      <label className="block text-sm font-medium mb-2" data-id="element-6623">
                        To
                      </label>
                      <input type="date" className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:border-primary transition-colors" data-id="element-6624" />
                    </div>
                  </div>
                </div>

                <button onClick={nextStep} className="text-sm text-textMuted hover:text-textMain transition-colors" data-id="element-6625">
                  Skip for now →
                </button>
              </motion.div>}

            {/* Step 5: First Objective */}
            {step === 5 && <motion.div key="step5" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-6626">
                <h2 className="text-3xl font-bold mb-4" data-id="element-6627">
                  What do you want to do first?
                </h2>
                <p className="text-textMuted mb-8" data-id="element-6628">
                  We'll help you get started.
                </p>

                <div className="space-y-3" data-id="element-6629">
                  {role === 'artist' && ['Plan a tour', 'Find venues in my region', 'Set up my artist profile', 'Explore the platform'].map(option => <button key={option} onClick={nextStep} className="w-full p-4 bg-surface border border-border rounded-lg hover:border-primary transition-all text-left flex items-center justify-between group" data-id="element-6630">
                        <span data-id="element-6631">{option}</span>
                        <ChevronRight className="w-5 h-5 text-textMuted group-hover:text-primary transition-colors" data-id="element-6632" />
                      </button>)}

                  {role === 'venue' && ['Fill an open date', 'Browse artists', 'Set up my venue profile', 'Explore the platform'].map(option => <button key={option} onClick={nextStep} className="w-full p-4 bg-surface border border-border rounded-lg hover:border-primary transition-all text-left flex items-center justify-between group" data-id="element-6633">
                        <span data-id="element-6634">{option}</span>
                        <ChevronRight className="w-5 h-5 text-textMuted group-hover:text-primary transition-colors" data-id="element-6635" />
                      </button>)}

                  {role === 'agent' && ['Set up my roster', 'Start a campaign', 'Invite team members', 'Explore the platform'].map(option => <button key={option} onClick={nextStep} className="w-full p-4 bg-surface border border-border rounded-lg hover:border-primary transition-all text-left flex items-center justify-between group" data-id="element-6636">
                        <span data-id="element-6637">{option}</span>
                        <ChevronRight className="w-5 h-5 text-textMuted group-hover:text-primary transition-colors" data-id="element-6638" />
                      </button>)}
                </div>
              </motion.div>}

            {/* Step 6: Success */}
            {step === 6 && <motion.div key="step6" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.4
          }} className="text-center" data-id="element-6639">
                <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6" data-id="element-6640">
                  <Check className="w-10 h-10 text-primary" data-id="element-6641" />
                </div>
                <h2 className="text-3xl font-bold mb-4" data-id="element-6642">You're all set!</h2>
                <p className="text-lg text-textMuted mb-10" data-id="element-6643">
                  Welcome to Booker. Let's start building your{' '}
                  {role === 'artist' ? 'tour' : role === 'venue' ? 'calendar' : 'roster'}
                  .
                </p>

                <button onClick={handleComplete} className="px-8 py-4 text-base font-medium text-background bg-primary rounded-lg hover:bg-primaryHover transition-all shadow-[0_0_20px_rgba(245,158,11,0.3)] inline-flex items-center gap-2" data-id="element-6644">
                  Go to Dashboard
                  <ChevronRight className="w-4 h-4" data-id="element-6645" />
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation */}
      {step < 6 && <div className="p-6 border-t border-border flex items-center justify-between" data-id="element-6646">
          <button onClick={prevStep} disabled={step === 1} className="px-4 py-2 text-sm font-medium text-textMuted hover:text-textMain transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-6647">
            <ChevronLeft className="w-4 h-4" data-id="element-6648" />
            Back
          </button>

          <button onClick={nextStep} disabled={step === 1 && !role} className="px-6 py-2 text-sm font-medium text-background bg-primary rounded-lg hover:bg-primaryHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-6649">
            Continue
            <ChevronRight className="w-4 h-4" data-id="element-6650" />
          </button>
        </div>}
    </div>;
}